import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { HStack } from '@components/base/layout';
import { useAdminListPage } from '../../hooks/useAdminListPage';
import { AdminListPage } from '../../components/AdminListPage';
import { erpErrorMappingsService } from '../../api/erpErrorMappingsService';
import type { ErpErrorMapping } from '../../api/erpErrorMappingsService';

const MATCH_TYPE_LABELS: Record<ErpErrorMapping['match_type'], string> = {
  http_status: 'HTTP Status',
  http_status_range: 'HTTP Status Range',
  error_code: 'Error Code',
  error_message: 'Error Message',
  body_contains: 'Body Contains',
  composite: 'Composite',
};

const LOG_LEVEL_VARIANTS: Record<ErpErrorMapping['log_level'], 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary'> = {
  debug: 'default',
  info: 'info',
  warning: 'warning',
  error: 'error',
  critical: 'error',
};

interface ErrorMappingsListPanelProps {
  readonly registryId: string;
}

export function ErrorMappingsListPanel({ registryId }: ErrorMappingsListPanelProps) {
  const navigate = useNavigate();

  const listPage = useAdminListPage<ErpErrorMapping, string>({
    queryKey: ['admin', 'erp', 'registries', registryId, 'error-mappings'],
    queryFn: () => erpErrorMappingsService.list(registryId),
    deleteFn: (mappingSlug) => erpErrorMappingsService.remove(registryId, mappingSlug),
    getDeleteId: (mapping) => mapping.slug,
    extraInvalidateKeys: [
      ['admin', 'erp', 'registries', registryId],
    ],
    transformData: (items) =>
      [...items].sort((a, b) => a.priority - b.priority),
  });

  return (
    <AdminListPage<ErpErrorMapping>
      variant="panel"
      title="Error Mappings"
      loadingLabel="Loading error mappings..."
      errorTitle="Failed to load error mappings"
      errorFallback="An unexpected error occurred while loading error mappings."
      emptyTitle="No error mappings found"
      emptyDescription="Create your first error mapping to define how ERP errors are translated."
      createLabel="Create Error Mapping"
      createButtonSize="sm"
      onCreateClick={() =>
        navigate(`/admin/erp/registries/${registryId}/error-mappings/create`)
      }
      items={listPage.items}
      getRowKey={(mapping) => mapping.id}
      isLoading={listPage.isLoading}
      error={listPage.error}
      deleteModalTitle="Delete Error Mapping"
      deleteTarget={listPage.deleteTarget}
      getDeleteTargetName={(mapping) => mapping.origami_error_code}
      deleteNoun="error mapping"
      onDeleteCancel={listPage.cancelDelete}
      onDeleteConfirm={listPage.confirmDelete}
      isDeleting={listPage.isDeleting}
      isDeleteError={listPage.isDeleteError}
      deleteError={listPage.deleteError}
      renderHead={() => (
        <>
          <Table.HeaderCell width={80}>Priority</Table.HeaderCell>
          <Table.HeaderCell>Match Type</Table.HeaderCell>
          <Table.HeaderCell>Error Code</Table.HeaderCell>
          <Table.HeaderCell>Error Message</Table.HeaderCell>
          <Table.HeaderCell align="center" width={100}>
            HTTP Status
          </Table.HeaderCell>
          <Table.HeaderCell align="center" width={100}>
            Retryable
          </Table.HeaderCell>
          <Table.HeaderCell align="center" width={100}>
            Log Level
          </Table.HeaderCell>
          <Table.HeaderCell align="right" width={100}>
            Actions
          </Table.HeaderCell>
        </>
      )}
      renderRow={(mapping) => (
        <>
          <Table.Cell>
            <Text weight="medium">{mapping.priority}</Text>
          </Table.Cell>
          <Table.Cell>
            <Badge variant="info" size="sm">
              {MATCH_TYPE_LABELS[mapping.match_type]}
            </Badge>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm">{mapping.origami_error_code}</Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm" color="muted" truncate>
              {mapping.origami_error_message}
            </Text>
          </Table.Cell>
          <Table.Cell align="center">
            <Text size="sm">
              {mapping.http_status ?? '\u2014'}
            </Text>
          </Table.Cell>
          <Table.Cell align="center">
            <Badge
              variant={mapping.is_retryable ? 'success' : 'default'}
              size="sm"
            >
              {mapping.is_retryable ? 'Yes' : 'No'}
            </Badge>
          </Table.Cell>
          <Table.Cell align="center">
            <Badge
              variant={LOG_LEVEL_VARIANTS[mapping.log_level]}
              size="sm"
            >
              {mapping.log_level}
            </Badge>
          </Table.Cell>
          <Table.Cell align="right">
            <HStack gap={1} justify="end">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit error mapping ${mapping.origami_error_code}`}
                leftIcon={<Icon icon={Pencil} size="sm" />}
                onClick={() =>
                  navigate(
                    `/admin/erp/registries/${registryId}/error-mappings/${mapping.slug}/edit`,
                  )
                }
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete error mapping ${mapping.origami_error_code}`}
                leftIcon={<Icon icon={Trash2} size="sm" color="error" />}
                onClick={() => listPage.setDeleteTarget(mapping)}
              />
            </HStack>
          </Table.Cell>
        </>
      )}
    />
  );
}
